import React, { useState, useEffect, useRef, useMemo } from 'react';
import { ChevronDown, X } from 'lucide-react';

const SearchAutocomplete = ({
  placeholder,
  value,
  options = [],
  onSelect,
  className = "",
  inputClassName = "",
  dropdownClassName = "",
  disabled = false
}) => {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [updateCount, setUpdateCount] = useState(0);
  const containerRef = useRef(null);
  const debounceTimerRef = useRef(null);

  const storageKey = `search_pref_${(placeholder || 'default').toLowerCase().trim()}`;

  const getLocalSearchScore = (key) => {
    try {
      const data = JSON.parse(localStorage.getItem(storageKey) || '{}');
      return data[String(key).toLowerCase()] || 0;
    } catch {
      return 0;
    }
  };

  const incrementLocalSearchScore = (key) => {
    try {
      const data = JSON.parse(localStorage.getItem(storageKey) || '{}');
      const k = String(key).toLowerCase();
      data[k] = (data[k] || 0) + 1;
      localStorage.setItem(storageKey, JSON.stringify(data));
    } catch {}
  };

  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, []);

  // Sync internal query text when value prop changes externally (and user is not actively typing)
  useEffect(() => {
    if (isTyping) return;
    if (value === '' || value === null || value === undefined) {
      setQuery('');
      return;
    }
    const matchedOption = options.find(opt => {
      if (typeof opt === 'object' && opt !== null) {
        return String(opt.id) === String(value) || opt.name?.toLowerCase() === String(value).toLowerCase();
      }
      return String(opt).toLowerCase() === String(value).toLowerCase();
    });

    if (matchedOption) {
      setQuery(typeof matchedOption === 'object' ? matchedOption.name : String(matchedOption));
    } else {
      const isNumericId = /^\d+$/.test(String(value).trim());
      if (isNumericId && options.length === 0) {
        setQuery('');
      } else {
        setQuery(String(value));
      }
    }
  }, [value, options, isTyping]);

  // Click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setIsTyping(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Sort options by local popularity score
  const sortedOptions = useMemo(() => {
    if (!Array.isArray(options)) return [];
    return [...options].sort((a, b) => {
      const keyA = typeof a === 'object' ? (a.name || a.id) : String(a);
      const keyB = typeof b === 'object' ? (b.name || b.id) : String(b);
      const scoreA = getLocalSearchScore(keyA);
      const scoreB = getLocalSearchScore(keyB);
      return scoreB - scoreA;
    });
  }, [options, isOpen, updateCount]);

  // Filter options based on query when typing
  const filteredOptions = useMemo(() => {
    if (isTyping && query.trim() !== '') {
      return sortedOptions.filter(opt => {
        const label = typeof opt === 'object' ? opt.name : String(opt);
        return label.toLowerCase().includes(query.trim().toLowerCase());
      });
    }
    return sortedOptions;
  }, [sortedOptions, query, isTyping]);

  // Display top 5 on initial open/focus, or all matching suggestions when typing
  const displayedOptions = useMemo(() => {
    if (isTyping && query.trim() !== '') {
      return filteredOptions;
    }
    return filteredOptions.slice(0, 5);
  }, [filteredOptions, isTyping, query]);

  const handleInputChange = (e) => {
    const val = e.target.value;
    setIsTyping(true);
    setQuery(val);
    setIsOpen(true);
    
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      const exactMatch = options.find(opt => {
        const label = typeof opt === 'object' ? opt.name : String(opt);
        return label.toLowerCase() === val.trim().toLowerCase();
      });

      if (exactMatch) {
        const matchVal = typeof exactMatch === 'object' ? exactMatch.id : String(exactMatch);
        const matchLabel = typeof exactMatch === 'object' ? exactMatch.name : String(exactMatch);
        if (onSelect) onSelect(matchVal, matchLabel, exactMatch);
      }
    }, 200);
  };

  const handleSelectOption = (opt) => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    const label = typeof opt === 'object' ? opt.name : String(opt);
    const val = typeof opt === 'object' ? opt.id : String(opt);
    setQuery(label);
    setIsTyping(false);
    setIsOpen(false);
    incrementLocalSearchScore(label);
    if (typeof opt === 'object' && opt.name) {
      incrementLocalSearchScore(opt.name);
    }
    setUpdateCount(c => c + 1);
    if (onSelect) {
      onSelect(val, label, opt);
    }
  };

  const handleClear = (e) => {
    e.stopPropagation();
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    setQuery('');
    setIsTyping(false);
    setIsOpen(false);
    if (onSelect) {
      onSelect('', '', null);
    }
  };

  return (
    <div ref={containerRef} className={`relative w-full ${className}`}>
      <div className="relative flex items-center w-full">
        <input
          type="text"
          value={query}
          onChange={handleInputChange}
          onClick={() => {
            if (!isOpen) {
              setIsOpen(true);
              setIsTyping(false);
            }
          }}
          onFocus={() => {
            setIsOpen(true);
            setIsTyping(false);
          }}
          placeholder={placeholder}
          disabled={disabled}
          className={`truncate ${inputClassName}`}
        />
        {query ? (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-1 sm:right-3 text-gray-400 hover:text-gray-600 p-0.5 rounded-full cursor-pointer focus:outline-none flex-shrink-0"
          >
            <X size={12} className="sm:w-3.5 sm:h-3.5" />
          </button>
        ) : (
          <ChevronDown
            size={12}
            className={`absolute right-1 sm:right-3 text-gray-400 pointer-events-none transition-transform duration-200 sm:w-3.5 sm:h-3.5 ${isOpen ? 'rotate-180 text-[#2ab391]' : ''}`}
          />
        )}
      </div>

      {isOpen && !disabled && (
        <div className={`absolute top-full mt-1.5 bg-white border border-gray-200 rounded-xl shadow-xl max-h-60 overflow-y-auto z-[9999] py-1 text-left ${dropdownClassName || 'left-0 right-0'}`}>
          {displayedOptions.length > 0 ? (
            displayedOptions.map((opt, idx) => {
              const label = typeof opt === 'object' ? opt.name : String(opt);
              const val = typeof opt === 'object' ? opt.id : String(opt);
              const isSelected = String(value).toLowerCase() === String(val).toLowerCase() || String(value).toLowerCase() === label.toLowerCase();
              return (
                <div
                  key={idx}
                  onClick={() => handleSelectOption(opt)}
                  className={`px-3.5 py-2.5 text-xs sm:text-sm font-medium cursor-pointer transition-colors flex items-center justify-between whitespace-nowrap ${
                    isSelected ? 'bg-[#eaf7f4] text-[#2ab391] font-semibold' : 'text-gray-700 hover:bg-gray-50 hover:text-[#2ab391]'
                  }`}
                >
                  <span className="truncate">{label}</span>
                </div>
              );
            })
          ) : (
            <div className="px-4 py-3 text-xs text-gray-400 italic text-center">
              No results found
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchAutocomplete;
